import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useExpandable } from '../../hooks/useExpandable';
import {
  ADMIN_DASHBOARD_ITEM,
  ADMIN_NAV_GROUPS,
  findActiveGroupKey,
  isNavItemActive,
} from '../../config/adminNav';

const COLLAPSED_STORAGE_KEY = 'neia_admin_sidebar_collapsed';
const DashboardIcon = ADMIN_DASHBOARD_ITEM.icon;

const AdminSidebar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isSuperAdmin } = useAuth();

  const getInitialOpenState = () => {
    try {
      return localStorage.getItem(COLLAPSED_STORAGE_KEY) !== '1';
    } catch {
      return true;
    }
  };

  const { isExpanded: isOpen, toggleExpanded: toggleCollapsed } = useExpandable(getInitialOpenState());

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, isOpen ? '0' : '1');
    } catch {
      // ignore write failures (private mode, storage disabled)
    }
  }, [isOpen]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const activeKey = findActiveGroupKey(location.pathname);
    return activeKey ? { [activeKey]: true } : {};
  });

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const visibleGroups = ADMIN_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.requiredRole || (item.requiredRole === 'super_admin' && isSuperAdmin)),
  })).filter((group) => group.items.length > 0);

  const itemLinkClass = (active: boolean, collapsedMode: boolean) =>
    `flex items-center gap-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
      collapsedMode ? 'justify-center p-2.5' : 'px-3 py-2'
    } ${active ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'}`;

  return (
    <aside
      className={`hidden lg:flex lg:flex-col shrink-0 bg-white border-r border-gray-200 py-4 transition-all duration-200 ${
        isOpen ? 'w-64 px-3' : 'w-[4.5rem] px-2'
      }`}
    >
      <div className={`flex items-center mb-3 ${isOpen ? 'justify-end' : 'justify-center'}`}>
        <button
          type="button"
          onClick={toggleCollapsed}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          aria-label={isOpen ? t('admin.sidebarCollapse') : t('admin.sidebarExpand')}
          title={isOpen ? t('admin.sidebarCollapse') : t('admin.sidebarExpand')}
        >
          {isOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto space-y-1">
        <Link
          to={ADMIN_DASHBOARD_ITEM.href}
          title={!isOpen ? t(ADMIN_DASHBOARD_ITEM.labelKey) : undefined}
          className={itemLinkClass(isNavItemActive(ADMIN_DASHBOARD_ITEM, location.pathname), !isOpen)}
        >
          <DashboardIcon className="w-5 h-5 shrink-0" />
          {isOpen && <span className="truncate">{t(ADMIN_DASHBOARD_ITEM.labelKey)}</span>}
        </Link>

        <div className="my-2 border-t border-gray-100" />

        {visibleGroups.map((group) => {
          const GroupIcon = group.icon;
          const isGroupOpen = !!openGroups[group.key];
          const groupHasActiveItem = group.items.some((item) => isNavItemActive(item, location.pathname));

          if (!isOpen) {
            return (
              <div key={group.key} className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isNavItemActive(item, location.pathname);
                  return (
                    <Link
                      key={item.key}
                      to={item.href}
                      title={t(item.labelKey)}
                      className={itemLinkClass(active, true)}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                    </Link>
                  );
                })}
              </div>
            );
          }

          return (
            <div key={group.key}>
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className={`flex items-center w-full gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                  groupHasActiveItem ? 'text-primary-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <GroupIcon className="w-4.5 h-4.5 shrink-0" />
                <span className="flex-1 text-left truncate">{t(group.labelKey)}</span>
                <ChevronDown
                  className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isGroupOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isGroupOpen && (
                <div className="mt-1 ml-2 pl-3 border-l border-gray-100 space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isNavItemActive(item, location.pathname);
                    return (
                      <Link key={item.key} to={item.href} className={itemLinkClass(active, false)}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{t(item.labelKey)}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default AdminSidebar;
